import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import { Badge, Text } from '../../components/UI';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { IReleaseNote } from '../../services';

type BadgeColor = 'zinc' | 'red' | 'emerald' | 'blue';

// #region categoryBadgeColor
export const categoryBadgeColor = (category: string): BadgeColor => {
    switch (category) {
        case 'feature': return 'emerald';
        case 'bug_fix': return 'red';
        case 'improvement': return 'blue';
        default: return 'zinc';
    }
};

// #region categoryLabel
export const categoryLabel = (category: string): string => {
    switch (category) {
        case 'feature': return 'Feature';
        case 'bug_fix': return 'Bug Fix';
        case 'improvement': return 'Improvement';
        default: return category;
    }
};

// #region mdComponents
export const mdComponents: Components = {
    h1: ({ children }) => (
        <h1 className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mb-3">{children}</h1>
    ),
    h2: ({ children }) => (
        <h2 className="text-lg font-semibold text-emerald-600 dark:text-emerald-400 mt-4 mb-2">{children}</h2>
    ),
    h3: ({ children }) => (
        <h3 className="text-base font-semibold text-emerald-600 dark:text-emerald-400 mt-3 mb-1">{children}</h3>
    ),
    p: ({ children }) => (
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 leading-relaxed">{children}</p>
    ),
    ul: ({ children }) => (
        <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>
    ),
    ol: ({ children }) => (
        <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>
    ),
    li: ({ children }) => (
        <li className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{children}</li>
    ),
    strong: ({ children }) => (
        <strong className="font-semibold text-gray-900 dark:text-gray-100">{children}</strong>
    ),
    code: ({ children }) => (
        <code className="text-xs bg-surface-muted dark:bg-stone-800 text-gray-800 dark:text-gray-200 px-1 py-0.5 rounded">{children}</code>
    ),
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-emerald-600 dark:text-emerald-400 underline">{children}</a>
    ),
};

// #region ReleaseNoteEntryProps
interface ReleaseNoteEntryProps {
    note: IReleaseNote;
    isNew?: boolean;
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    showConnector?: boolean;
}

// #region ReleaseNoteEntry
export const ReleaseNoteEntry: React.FunctionComponent<ReleaseNoteEntryProps> = ({
    note,
    isNew = true,
    isExpanded = true,
    onToggleExpand,
    showConnector = false,
}): React.ReactElement => {
    const canCollapse = !isNew && onToggleExpand;

    return (
        <div className="relative flex gap-x-4">
            {/* Timeline dot + connector */}
            <div className="flex flex-col items-center">
                <div className={`w-3 h-3 rounded-full mt-1.5 shrink-0 ${isNew ? 'bg-emerald-500' : 'bg-gray-400 dark:bg-gray-600'}`} />
                {showConnector && (
                    <div className="w-px flex-1 bg-surface-strong dark:bg-gray-700" />
                )}
            </div>

            {/* Content */}
            <div className="flex-1 pb-8">
                {/* Title row — always fully visible */}
                <div className="flex items-center gap-x-2 flex-wrap mb-1">
                    <Text className="font-semibold text-base">{note.title || 'Untitled'}</Text>
                    <Badge color={categoryBadgeColor(note.category)}>
                        {categoryLabel(note.category)}
                    </Badge>
                    <span className="text-xs font-mono dark:text-gray-400 text-gray-500 bg-surface-muted dark:bg-stone-800 px-1.5 py-0.5 rounded">
                        v{note.version || '0.0.0'}
                    </span>
                    {!isNew && (
                        <span className="text-xs italic text-gray-400 dark:text-gray-500">(already viewed)</span>
                    )}
                </div>
                <Text className="text-xs dark:text-gray-400 text-gray-500 mb-2">
                    {note.created_at
                        ? new Date(note.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                        : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                    }
                </Text>

                {isExpanded ? (
                    <>
                        <div className="prose prose-sm dark:prose-invert max-w-none mt-2">
                            {note.description?.trim() ? (
                                <ReactMarkdown components={mdComponents}>{note.description}</ReactMarkdown>
                            ) : (
                                <Text className="text-gray-400 italic">No description content</Text>
                            )}
                            {note.admin_notes?.trim() && (
                                <div className="mt-4 p-3 rounded-lg border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20">
                                    <Text className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-2">Admin Notes</Text>
                                    <ReactMarkdown components={mdComponents}>{note.admin_notes}</ReactMarkdown>
                                </div>
                            )}
                            {note.superuser_notes?.trim() && (
                                <div className="mt-4 p-3 rounded-lg border border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/20">
                                    <Text className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide mb-2">Superuser Notes</Text>
                                    <ReactMarkdown components={mdComponents}>{note.superuser_notes}</ReactMarkdown>
                                </div>
                            )}
                        </div>
                        {canCollapse && (
                            <button
                                onClick={onToggleExpand}
                                className="mt-3 inline-flex items-center gap-x-1 text-sm font-medium text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 hover:bg-surface-muted dark:hover:bg-stone-800 transition-colors bg-transparent"
                            >
                                Show less
                                <ChevronUpIcon className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </>
                ) : (
                    <div className="mt-2">
                        {/*
                          The truncation fade is a mask on the text itself, not an
                          opaque gradient laid over it. An overlay has to hard-code
                          the surface colour behind it, which was wrong everywhere
                          it rendered (the page is neutral-800, the header dropdown
                          panel is stone-600) and read as a heavy inset shadow. A
                          mask fades to real transparency, so it is correct on any
                          background and stays subtle.
                        */}
                        <div className="overflow-hidden max-h-20">
                            <div
                                className="prose prose-sm dark:prose-invert max-w-none opacity-80"
                                style={{
                                    maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
                                    WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)'
                                } as React.CSSProperties}
                            >
                                <ReactMarkdown components={mdComponents}>{note.description}</ReactMarkdown>
                            </div>
                        </div>
                        {onToggleExpand && (
                            <button
                                onClick={onToggleExpand}
                                className="mt-1 inline-flex items-center gap-x-1 text-sm font-medium text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 hover:bg-surface-muted dark:hover:bg-stone-800 transition-colors bg-transparent"
                            >
                                Read more
                                <ChevronDownIcon className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
